<script setup lang="ts">
import { computed } from 'vue';
import {
  instanceOfTrackItMeasurementParameterAreaDto,
  instanceOfTrackItMeasurementParameterBooleanDto,
  instanceOfTrackItMeasurementParameterDoubleDto,
  instanceOfTrackItMeasurementParameterLongDto,
  instanceOfTrackItMeasurementParameterModalityDto,
  instanceOfTrackItMeasurementPropertyFilterDto,
  Modality,
  ParameterCombinationDto,
  TrackItMeasurementFilterForCombinationDto,
  TrackItMeasurementParameterFilterDto,
  TrackItMeasurementParameterFilterWithUserValueDto,
  TrackItMeasurementPropertyFilterDto,
} from '@/api/generator';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import VeeSelect from '@/components/forms/Select/VeeSelect.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { I18nKey } from '@/utils/typings';
import {
  numberFormatOfMeasurementParameter,
  trackItFilterIdentityKey,
  trackItFiltersMatch,
  translateMeasurementPropertyName,
} from './trackItFilters';

const mode = useFormMode();

const props = defineProps<{
  name: string; // the combination's form path, e.g. `taskItemAreas[0].combinationTable.combinations[1]`
  combination: ParameterCombinationDto;
  selectedFilters: TrackItMeasurementParameterFilterDto[];
  selectedProperties: TrackItMeasurementPropertyFilterDto[];
  // The combination itself is disabled: everything is shown greyed out and can't be edited.
  disabled?: boolean;
}>();

const modalityOptions = Object.values(Modality);

const rows = computed(() =>
  props.selectedFilters
    .map((selected) =>
      props.combination.trackItFilters?.find(
        (filter): filter is TrackItMeasurementParameterFilterWithUserValueDto =>
          !instanceOfTrackItMeasurementPropertyFilterDto(filter) &&
          'measurementParameter' in filter &&
          trackItFiltersMatch(filter, selected),
      ),
    )
    .filter(
      (row): row is TrackItMeasurementParameterFilterWithUserValueDto =>
        !!row?.measurementParameter,
    )
    // Read-only: there's nothing to un-ignore, so an ignored row is just noise.
    .filter((row) => mode.value !== 'details' || row.isEnabled),
);

const propertyRows = computed(() =>
  props.selectedProperties
    .map((selected) =>
      props.combination.trackItFilters?.find(
        (filter): filter is TrackItMeasurementPropertyFilterDto =>
          instanceOfTrackItMeasurementPropertyFilterDto(filter) &&
          trackItFiltersMatch(filter, selected),
      ),
    )
    .filter((row): row is TrackItMeasurementPropertyFilterDto => !!row)
    .filter((row) => mode.value !== 'details' || row.isEnabled),
);

// Rows are matched by identity, so their form path comes from their position in the combination.
function filterPath(row: TrackItMeasurementFilterForCombinationDto) {
  return `${props.name}.trackItFilters[${props.combination.trackItFilters?.indexOf(row)}]`;
}

function isRowDisabled(row: TrackItMeasurementFilterForCombinationDto) {
  return props.disabled || !row.isEnabled;
}

function unitOf(row: TrackItMeasurementParameterFilterWithUserValueDto): string | undefined {
  const param = row.measurementParameter;
  if (!param) return undefined;
  if (instanceOfTrackItMeasurementParameterAreaDto(param)) return param.unit ?? undefined;
  if (instanceOfTrackItMeasurementParameterDoubleDto(param)) return param.unit ?? undefined;
  if (instanceOfTrackItMeasurementParameterLongDto(param)) return param.unit ?? undefined;
  return undefined;
}
</script>

<template>
  <v-card v-if="rows.length || propertyRows.length" class="container-level-2">
    <v-card-item>
      <v-card-title>
        <h5>{{ $t('TrackItFilters' as I18nKey) }}</h5>
      </v-card-title>
    </v-card-item>

    <v-card-text>
      <v-row>
        <v-col v-if="rows.length">
          <h6 class="mb-2">{{ $t('TrackItMeasurementParameter_other' as I18nKey) }}</h6>
          <v-list class="bg-transparent" density="compact">
            <v-list-item v-for="row in rows" :key="trackItFilterIdentityKey(row)" class="pl-0">
              <div class="d-flex align-center ga-4">
                <VeeCheckbox
                  v-if="mode !== 'details'"
                  :name="`${filterPath(row)}.isEnabled`"
                  hideDetails
                  :disabled="disabled"
                />

                <span class="condition-name" :class="{ 'condition-disabled': isRowDisabled(row) }">
                  {{ row.measurementParameter!.name }}
                  <InlineValidationError
                    :name="filterPath(row)"
                    variant="icon-only"
                    ignoreTouched
                  />
                </span>

                <VeeCheckbox
                  v-if="instanceOfTrackItMeasurementParameterBooleanDto(row.measurementParameter!)"
                  :name="`${filterPath(row)}.measurementParameter.value`"
                  hideDetails
                  class="value-field"
                  :disabled="isRowDisabled(row)"
                />

                <VeeNumberField
                  v-else-if="
                    instanceOfTrackItMeasurementParameterDoubleDto(row.measurementParameter!) ||
                    instanceOfTrackItMeasurementParameterLongDto(row.measurementParameter!)
                  "
                  :format="numberFormatOfMeasurementParameter(row.measurementParameter!)"
                  :name="`${filterPath(row)}.measurementParameter.value`"
                  density="compact"
                  hideDetails="auto"
                  class="value-field"
                  :disabled="isRowDisabled(row)"
                  :suffix="unitOf(row)"
                />

                <VeeSelect
                  v-else-if="
                    instanceOfTrackItMeasurementParameterModalityDto(row.measurementParameter!)
                  "
                  :name="`${filterPath(row)}.measurementParameter.value`"
                  :items="modalityOptions"
                  density="compact"
                  hideDetails="auto"
                  class="value-field"
                  :disabled="isRowDisabled(row)"
                />

                <div
                  v-else-if="
                    instanceOfTrackItMeasurementParameterAreaDto(row.measurementParameter!)
                  "
                  class="d-flex align-center ga-1"
                >
                  <VeeNumberField
                    :format="numberFormatOfMeasurementParameter(row.measurementParameter!)"
                    :name="`${filterPath(row)}.measurementParameter.valueX`"
                    density="compact"
                    hideDetails="auto"
                    :class="mode === 'details' ? undefined : 'value-field-area'"
                    :disabled="isRowDisabled(row)"
                  />
                  <span>x</span>
                  <VeeNumberField
                    :format="numberFormatOfMeasurementParameter(row.measurementParameter!)"
                    :name="`${filterPath(row)}.measurementParameter.valueY`"
                    density="compact"
                    hideDetails="auto"
                    :class="mode === 'details' ? undefined : 'value-field-area'"
                    :disabled="isRowDisabled(row)"
                  />
                  <span v-if="unitOf(row)" class="text-medium-emphasis">{{ unitOf(row) }}</span>
                </div>

                <VeeTextField
                  v-else
                  :name="`${filterPath(row)}.measurementParameter.value`"
                  density="compact"
                  hideDetails="auto"
                  class="value-field"
                  :disabled="isRowDisabled(row)"
                />
              </div>
            </v-list-item>
          </v-list>
        </v-col>

        <v-col v-if="propertyRows.length">
          <h6 class="mb-2">{{ $t('TrackItMeasuringProperty_other' as I18nKey) }}</h6>
          <v-list class="bg-transparent" density="compact">
            <v-list-item
              v-for="row in propertyRows"
              :key="trackItFilterIdentityKey(row)"
              class="pl-0"
            >
              <div class="d-flex align-center ga-4">
                <VeeCheckbox
                  v-if="mode !== 'details'"
                  :name="`${filterPath(row)}.isEnabled`"
                  hideDetails
                  :disabled="disabled"
                />

                <span class="condition-name" :class="{ 'condition-disabled': isRowDisabled(row) }">
                  {{ translateMeasurementPropertyName(row.measurementProperty!.name) }}
                  <InlineValidationError
                    :name="filterPath(row)"
                    variant="icon-only"
                    ignoreTouched
                  />
                </span>
                <VeeTextField
                  :name="`${filterPath(row)}.measurementProperty.value`"
                  density="compact"
                  hideDetails="auto"
                  class="value-field"
                  :disabled="isRowDisabled(row)"
                />
              </div>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
.condition-name {
  width: 12em;
}
.condition-disabled {
  color: rgb(var(--v-theme-primary-lighten-2));
}
.value-field {
  width: 12em;
  // v-input defaults to flex: 1 1 auto — the enum v-select (and any field in a row whose
  // sibling column is empty) would stretch far wider than the other value fields otherwise.
  flex: 0 0 auto;
}
.value-field-area {
  width: 6em;
  flex: 0 0 auto;
}
</style>
